import { create } from 'zustand';
import { Event, EventFilters, EventStats, Venue, AppSettings, ColorPreset } from '../types';
import { API_ENDPOINTS, apiCall, isScreenshotEnvironment } from '../config/api';

interface EventStore {
  events: Event[];
  filters: EventFilters;
  selectedEvent: Event | null;
  isEventDialogOpen: boolean;
  venues: Venue[];
  settings: AppSettings;
  colorPresets: ColorPreset[];
  
  // Event Actions
  addEvent: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  addMultipleEvents: (events: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>[]) => Promise<void>;
  updateEvent: (id: string, updates: Partial<Event>) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  loadEventsFromDatabase: () => Promise<void>;
  setFilters: (filters: Partial<EventFilters>) => void;
  setSelectedEvent: (event: Event | null) => void;
  setEventDialogOpen: (isOpen: boolean) => void;
  
  // Venue Actions
  addVenue: (venue: Omit<Venue, 'id'>) => void;
  updateVenue: (id: string, updates: Partial<Venue>) => void;
  deleteVenue: (id: string) => void;
  
  // Settings Actions
  updateSettings: (settings: Partial<AppSettings>) => void;
  uploadLogo: (file: File) => Promise<void>;
  resetToDefaults: () => void;
  
  // Computed
  getFilteredEvents: () => Event[];
  getEventStats: () => EventStats;
  getEventsForDate: (date: string) => Event[];
  getVenueById: (id: string) => Venue | undefined;
  getEventColor: (event: Event) => string;
}

// Default venues
const defaultVenues: Venue[] = [
  {
    id: 'restaurant',
    name: 'Restaurant',
    type: 'restaurant',
    color: '#10b981', // emerald-500
    icon: '🍽️'
  },
  {
    id: 'bar',
    name: 'Bar',
    type: 'bar', 
    color: '#f59e0b', // amber-500
    icon: '🍺'
  },
  {
    id: 'banquet',
    name: 'Banquet Hall',
    type: 'banquet',
    color: '#6366f1', // indigo-500
    icon: '🎉'
  }
];

// Default settings with customer information and default logo
const defaultSettings: AppSettings = {
  storeName: 'My Restaurant',
  storeEmail: 'Mikeakanan@gmail.com',
  storePhone: '313.938.6666',
  storeAddress: '0000 Street Name, Farmington Hills Mi 48336',
  applicationTitle: 'Event Manager',
  applicationSubtitle: 'Professional Hospitality Event Management',
  logo: '/assets/default-logo.png', // Default logo that can be changed by customer
  themeColor: '#f59e0b',
  backgroundColor: '#0f172a', // Default dark slate background
  textColor: '#ffffff', // Default white text
  highlightTextColor: '#f59e0b', // Default orange highlights
  dbHost: '',
  dbUser: '',
  dbPassword: '',
  dbName: ''
};

// Color presets
const defaultColorPresets: ColorPreset[] = [
  { id: '1', name: 'Red', color: '#ef4444' },
  { id: '2', name: 'Orange', color: '#f97316' },
  { id: '3', name: 'Amber', color: '#f59e0b' },
  { id: '4', name: 'Yellow', color: '#eab308' },
  { id: '5', name: 'Lime', color: '#84cc16' },
  { id: '6', name: 'Green', color: '#22c55e' },
  { id: '7', name: 'Emerald', color: '#10b981' },
  { id: '8', name: 'Teal', color: '#14b8a6' },
  { id: '9', name: 'Cyan', color: '#06b6d4' },
  { id: '10', name: 'Sky', color: '#0ea5e9' },
  { id: '11', name: 'Blue', color: '#3b82f6' },
  { id: '12', name: 'Indigo', color: '#6366f1' },
  { id: '13', name: 'Violet', color: '#8b5cf6' },
  { id: '14', name: 'Purple', color: '#a855f7' },
  { id: '15', name: 'Fuchsia', color: '#d946ef' },
  { id: '16', name: 'Pink', color: '#ec4899' },
  { id: '17', name: 'Rose', color: '#f43f5e' },
  { id: '18', name: 'White', color: '#ffffff' },
  { id: '19', name: 'Black', color: '#000000' },
  { id: '20', name: 'Dark Navy', color: '#1e3a8a' }
];

// NO SAMPLE DATA - USER DATA ONLY
// All events come from user input and localStorage persistence

// API utilities imported from config/api.ts

export const useEventStore = create<EventStore>((set, get) => ({
  events: [],
  filters: {},
  selectedEvent: null,
  isEventDialogOpen: false,
  venues: defaultVenues,
  settings: defaultSettings,
  colorPresets: defaultColorPresets,

  // Bulk add events (for CSV import)
  addMultipleEvents: async (eventsData: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>[]) => {
    const newEvents: Event[] = eventsData.map(eventData => ({
      ...eventData,
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    // Add to local state immediately for UI responsiveness
    set((state) => ({
      events: [...state.events, ...newEvents],
    }));

    console.log(`✅ ${newEvents.length} events added locally`);

    // Try to save to backend database (but don't block on failure)
    const apiUrl = API_ENDPOINTS.eventsBulk;
    
    // Skip database save in screenshot/preview environments
    if (isScreenshotEnvironment()) {
      console.log('🖼️ Screenshot environment detected - using localStorage only');
      return;
    }
    
    // Attempt database save in background (non-blocking)
    try {
      const requestConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events: newEvents }),
      };
      
      const response = await apiCall(apiUrl, requestConfig);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Could not read error response');
        console.warn('⚠️ Bulk database save failed, but events saved locally:', {
          status: response.status,
          errorBody: errorText
        });
      } else {
        const responseData = await response.json().catch(() => ({ success: true }));
        console.log(`✅ ${newEvents.length} events also saved to database:`, responseData);
      }
    } catch (error) {
      console.warn(`⚠️ Bulk database save failed, but ${newEvents.length} events saved locally:`, {
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  },

  addEvent: async (eventData) => {
    const newEvent: Event = {
      ...eventData,
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const apiUrl = API_ENDPOINTS.events;

    if (isScreenshotEnvironment()) {
      console.log('🖼️ Screenshot environment detected - skipping network request.');
      set((state) => ({ events: [...state.events, newEvent] }));
      return;
    }

    const response = await apiCall(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newEvent),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error response');
      throw new Error(`Failed to create event: ${errorText}`);
    }

    set((state) => ({ events: [...state.events, newEvent] }));
  },

  updateEvent: async (id, updates) => {
    const updatedData = { ...updates, updatedAt: new Date().toISOString() };
    const apiUrl = API_ENDPOINTS.eventsById(id);

    if (isScreenshotEnvironment()) {
      console.log('🖼️ Screenshot environment detected - skipping network request.');
      set((state) => ({
        events: state.events.map((event) => (event.id === id ? { ...event, ...updatedData } : event)),
      }));
      return;
    }

    const response = await apiCall(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedData),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error response');
      throw new Error(`Failed to update event: ${errorText}`);
    }

    set((state) => ({
      events: state.events.map((event) => (event.id === id ? { ...event, ...updatedData } : event)),
    }));
  },

  deleteEvent: async (id) => {
    const apiUrl = API_ENDPOINTS.eventsById(id);

    if (isScreenshotEnvironment()) {
      console.log('🖼️ Screenshot environment detected - skipping network request.');
      set((state) => ({ events: state.events.filter((event) => event.id !== id) }));
      return;
    }

    const response = await apiCall(apiUrl, { method: 'DELETE' });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error response');
      throw new Error(`Failed to delete event: ${errorText}`);
    }

    set((state) => ({ events: state.events.filter((event) => event.id !== id) }));
  },

  // Load events from database (non-blocking, fallback to localStorage)
  loadEventsFromDatabase: async () => {
    const apiUrl = API_ENDPOINTS.events;

    if (isScreenshotEnvironment()) {
      console.log('🖼️ Screenshot environment detected - skipping network request.');
      return;
    }

    const response = await apiCall(apiUrl);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error response');
      throw new Error(`Failed to load events: ${errorText}`);
    }

    const data = await response.json();
    set({ events: data.events || [] });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  setSelectedEvent: (event) => {
    set({ selectedEvent: event });
  },

  setEventDialogOpen: (isOpen) => {
    set({ isEventDialogOpen: isOpen });
  },

  addVenue: (venueData) => {
    const newVenue: Venue = {
      ...venueData,
      id: `venue-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    set((state) => ({
      venues: [...state.venues, newVenue],
    }));
  },

  updateVenue: (id, updates) => {
    set((state) => ({
      venues: state.venues.map((venue) =>
        venue.id === id ? { ...venue, ...updates } : venue
      ),
    }));
  },

  deleteVenue: (id) => {
    set((state) => ({
      venues: state.venues.filter((venue) => venue.id !== id),
    }));
  },

  updateSettings: (updates) => {
    set((state) => ({
      settings: { ...state.settings, ...updates },
    }));
  },

  resetToDefaults: () => {
    set({ settings: { ...defaultSettings } });
  },

  uploadLogo: async (file) => {
    try {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        set((state) => ({
          settings: { ...state.settings, logo: base64 },
        }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
    }
  },

  getFilteredEvents: () => {
    const { events, filters } = get();
    
    return events.filter((event) => {
      // Venue filter
      if (filters.venue && event.venue !== filters.venue && event.venueId !== filters.venue) {
        return false;
      }
      
      // Status filter
      if (filters.status && event.status !== filters.status) {
        return false;
      }
      
      // Payment status filter
      if (filters.paymentStatus && event.paymentStatus !== filters.paymentStatus) {
        return false;
      }
      
      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        
        // Normalize phone numbers for searching
        const normalizePhone = (phone: string) => phone.replace(/[\s\-\(\)\.]/g, '');
        const normalizedQuery = normalizePhone(query);
        const normalizedEventPhone = normalizePhone(event.contact.phone);
        
        return (
          event.title.toLowerCase().includes(query) ||
          event.contact.name.toLowerCase().includes(query) ||
          event.contact.phone.includes(query) ||
          normalizedEventPhone.includes(normalizedQuery) ||
          event.contact.email.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  },

  getEventStats: () => {
    const events = get().getFilteredEvents();
    
    return events.reduce((stats, event) => {
      // Status counts
      stats[event.status]++;
      
      // Payment counts
      stats[event.paymentStatus]++;
      
      // Revenue calculations
      const eventTotal = event.pricing?.total || 0;
      const amountPaid = event.pricing?.amountPaid || 0;
      const remainingBalance = event.pricing?.remainingBalance || 0;
      
      if (event.paymentStatus === 'paid') {
        stats.totalRevenue += eventTotal;
      } else {
        stats.totalRevenue += amountPaid;
        stats.totalOutstanding += remainingBalance;
      }
      
      return stats;
    }, {
      confirmed: 0,
      pending: 0,
      cancelled: 0,
      closed: 0,
      paid: 0,
      unpaid: 0,
      totalRevenue: 0,
      totalOutstanding: 0,
    } as EventStats);
  },

  getEventsForDate: (date) => {
    // Get events for a specific date with ALL filters applied
    const filteredEvents = get().getFilteredEvents();
    
    // Return only events for the requested date from the filtered results
    return filteredEvents.filter((event) => event.date === date);
  },

  getVenueById: (id) => {
    const venues = get().venues;
    return venues.find((venue) => venue.id === id);
  },

  getEventColor: (event) => {
    if (event.color) {
      return event.color;
    }
    const venue = get().getVenueById(event.venueId || event.venue);
    return venue?.color || '#6b7280'; // Default gray
  },
}));

// ===== HYBRID DATA PERSISTENCE =====
// Database-first storage with optional local cache (disabled by default)
const ENABLE_LOCAL_CACHE = false;

if (typeof window !== 'undefined') {
  // STEP 1: Load settings, venues, and events from localStorage (local preferences and fallback)
  const savedSettings = ENABLE_LOCAL_CACHE ? localStorage.getItem('eventManager_settings') : null;
  const savedVenues = ENABLE_LOCAL_CACHE ? localStorage.getItem('eventManager_venues') : null;

  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      useEventStore.setState((state) => ({ settings: { ...state.settings, ...parsed } }));
    } catch (error) {
      console.warn('⚠️ Failed to load saved settings:', error);
    }
  }

  if (savedVenues) {
    try {
      const parsed = JSON.parse(savedVenues);
      useEventStore.setState(() => ({ venues: parsed }));
    } catch (error) {
      console.warn('⚠️ Failed to load saved venues:', error);
    }
  }

  const loadEventsFromDB = async () => {
    try {
      await useEventStore.getState().loadEventsFromDatabase();
      if (ENABLE_LOCAL_CACHE) {
        const newEvents = useEventStore.getState().events;
        localStorage.setItem('eventManager_events', JSON.stringify(newEvents));
      }
    } catch (error) {
      console.warn('⚠️ Database sync failed:', error);
    }
  };
  
  // Try to sync with database on app initialization (but don't block UI)
  loadEventsFromDB();
  
  // Auto-import sample events if no events exist
  const autoImportEvents = async () => {
    const currentEvents = useEventStore.getState().events;
    if (currentEvents.length === 0) {
      try {
        const { getAutoImportEvents } = await import('../utils/autoImportEvents');
        const sampleEvents = getAutoImportEvents();
        if (sampleEvents.length > 0) {
          await useEventStore.getState().addMultipleEvents(sampleEvents);
          console.log(`✅ Auto-imported ${sampleEvents.length} sample events`);
        }
      } catch (error) {
        console.warn('⚠️ Failed to auto-import sample events:', error);
      }
    }
  };
  
  // Auto-import after a short delay to allow localStorage to load first
  setTimeout(autoImportEvents, 1000);
  
  // STEP 3: Real-time saving for ALL data to localStorage
  let previousSettings = useEventStore.getState().settings;
  let previousVenues = useEventStore.getState().venues;
  let previousEvents = useEventStore.getState().events;
  
  if (ENABLE_LOCAL_CACHE) {
    const savedEvents = localStorage.getItem('eventManager_events');
    if (savedEvents) {
      try {
        const parsed = JSON.parse(savedEvents);
        useEventStore.setState(() => ({ events: parsed }));
      } catch (error) {
        console.warn('⚠️ Failed to load saved events:', error);
      }
    }
  }

  useEventStore.subscribe((state) => {
    if (ENABLE_LOCAL_CACHE) {
      if (state.settings !== previousSettings) {
        localStorage.setItem('eventManager_settings', JSON.stringify(state.settings));
        previousSettings = state.settings;
      }

      if (state.venues !== previousVenues) {
        localStorage.setItem('eventManager_venues', JSON.stringify(state.venues));
        previousVenues = state.venues;
      }

      if (state.events !== previousEvents) {
        localStorage.setItem('eventManager_events', JSON.stringify(state.events));
        previousEvents = state.events;
      }
    }
  });
  
  console.log('🛡️ Enhanced persistence system activated - Database sync with localStorage fallback');
}